import type { SimpleFieldConfig } from "../types/index";

/**
 * Determines if a cell can be edited based on field config and user role
 * Handles permission checks for different user types and entity contexts
 */
export function canEditCell(
  fieldConfig: SimpleFieldConfig,
  userRole?: string,
  entityName?: string
): boolean {
  // Field must be marked as editable
  if (!fieldConfig.editable) {
    return false;
  }

  // Disable inline editing for client users
  const isClientUser = userRole === "client_user";
  if (isClientUser) {
    return false;
  }

  // Disable inline editing for WTC employees on contacts table
  const isWtcEmployee = userRole === "wtc_employee";
  const isContactsTable = entityName === "contacts";
  if (isWtcEmployee && isContactsTable) {
    return false;
  }

  return true;
}

/**
 * Determines if a field should be treated as a textarea field
 * Textarea fields have special handling for blur events and button clicks
 */
export function isTextareaField(fieldConfig: SimpleFieldConfig, fieldId: string): boolean {
  return (
    fieldConfig.type === "textarea" ||
    fieldId === "body" ||
    fieldId?.includes("description") ||
    fieldId?.includes("notes") ||
    fieldId?.includes("content")
  );
}

/**
 * Determines if a field uses auto-save (vs manual save/cancel buttons)
 * Auto-save fields: selects, booleans, user/ticket/company/contact pickers, multi-user
 * Manual-save fields: text, textarea, date, datetime, number
 */
export function isAutoSaveField(fieldConfig: SimpleFieldConfig, fieldId: string): boolean {
  return (
    (fieldConfig.type === "select" && !!fieldConfig.options) ||
    fieldConfig.type === "boolean" ||
    fieldConfig.type === "multi-user" ||
    fieldId === "assigned_to" ||
    fieldId === "user_id" ||
    fieldId?.includes("user") ||
    fieldId?.includes("assign") ||
    fieldId === "ticket_id" ||
    fieldId?.includes("ticket") ||
    fieldId === "company_id" ||
    fieldId?.includes("company") ||
    fieldId === "contact_id" ||
    fieldId?.includes("contact") ||
    fieldId === "customer_id" ||
    fieldId?.includes("customer")
  );
}
